import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { contextSelector } from 'selectors/routerSelectors'
import { contextConfigSelector } from 'selectors/contextSelectors'
import {
  compatibleWithCurrentScreenType,
  getCurrentScreenType
} from 'Helper/screenTypeHelpers'

export const usePatient = () => {
  // note, this depends on react-router but not all components will see route/url updates in real time
  // this works in the top level of main contexts nurseDashboard, patientDisplay, adminDisplay but may
  // require changes for it to work properly in other areas or more deeply nested. be sure to test that
  // it is updating as you expect
  const { patientId } = useParams()
  return patientId
}

export const useScreen = (findDefaultIfNonSelected = true) => {
  const { screen: screenPath } = useParams()
  const context = useContext()
  const contextConfigs = useSelector(contextConfigSelector) || {}
  const config = useMemo(() => contextConfigs[context], [context, contextConfigs]) || {}
  
  const currentScreen = useMemo(() => {
    let { screens = [] } = config
    screens = screens.filter(compatibleWithCurrentScreenType)
    // screen is either from the url
    // or if not that, the first one marked as default
    // or if not that the first one in the array
    if (screenPath) {
      return screens.find(screen => screen.path === screenPath)
    } else if (findDefaultIfNonSelected) {
      return screens.find(screen => {
        return screen.default
      }) || screens.length > 0
        ? screens[0]
        : {}
    } else {
      return {}
    }
  }, [config.screens, context, screenPath])

  return currentScreen
}

export const useUIConfig = (requestedContext = null) => {
  const currentScreenType = getCurrentScreenType()
  let context = useContext()

  if (requestedContext) {
    context = requestedContext
  }

  const contextConfigs = useSelector(contextConfigSelector) || {}
  const config = useMemo(() => contextConfigs[context], [context, contextConfigs]) || {}

  const workflows = useMemo(() => {
    const { workflows = [] } = config
    return workflows.filter(compatibleWithCurrentScreenType)
}, [config.workflows, currentScreenType])

  const windows = useMemo(() => {
    const { windows = [] } = config
    return windows.filter(compatibleWithCurrentScreenType)
  }, [config.windows, currentScreenType])

  const screens = useMemo(() => {
    const { screens = []} = config
    return screens.filter(compatibleWithCurrentScreenType)
  }, [config.screens, currentScreenType])

  const quickAccess = useMemo(() => {
    const { quickAccess = [] } = config
    return quickAccess.filter(compatibleWithCurrentScreenType)
  }, [config.quickAccess, currentScreenType])

  const {headerObject: header, headerName} = useMemo(() => {
    let { header } = config

    let headerName =
    currentScreenType == 'ipad' ? 'MenuBarHeader' : 'MMPatientDemographics'
    // we have some legacy header configs of various types we need to be able to handle here.
    // current header configs should be an array but previously we have used string and object as well.
    if (typeof header == 'object' && !Array.isArray(header)) {
      // we can just convert this one to an array and let it be handled in the array handler
      header = [header]
    }

    // if its a string, the header name is just the header itself.
    let headerObject = {}
    if (typeof header == 'string') {
      // this is to old to have a screen type and it makes no sense to not show a header at all for any screen type so
      // we will just use this as is
      headerName = header
      headerObject = { name: headerName }
    } else if (Array.isArray(header)) {
      // this is a current configuration, we just need to find the appropriate config and return the name
      const foundHeader = header.filter(compatibleWithCurrentScreenType)
      // should only ever be one option per screen type so select index 0
      if (foundHeader[0] && foundHeader[0].name) {
        headerName = foundHeader[0].name
        headerObject = foundHeader[0]
      }
    }

    return { headerObject, headerName }
  }, [config.header, currentScreenType])

  return {
    workflows,
    windows,
    screens,
    headerName,
    header,
    quickAccess
  }
}

export const useWorkflow = () => {
  const { workflow: workflowPath, step } = useParams()
  const context = useContext()

  const contextConfigs = useSelector(contextConfigSelector) || {}
  const config = useMemo(() => contextConfigs[context], [context, contextConfigs]) || {}

  const workflow = useMemo(
    () => {
      const {workflows = []} = config
      return workflows.find(workflow => workflow.path === workflowPath)
    },
    [config.workflows, context, workflowPath]
  )

  return { workflow, step }
}

export const useContext = () => {
  return useSelector(contextSelector)
}
